import {
  FormBuilder,
  FormHandler,
} from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import { CompanyConfigClientCreateFerryCompanyVehicleTicketRequest } from '@naus-code/naus-admin-types';
import { useRef } from 'react';

import { FormContent } from '~/components/Form/Form.Container';
import { ModalScrollView } from '~/components/Modals/Modal.utils';
import { companyConfigApi } from '~/screens/Configs/Company/CompanyConfigs.api';
import { translate } from '~/utils/translation.utils';

const updateVehicleTicketModalKey = 'UpdateVehicleTicket-ModalKey';

export const useUpdateVehicleTicketConfigModal = () => {
  const { openModal, closeModal } = useModal();

  return {
    open: ({
      companyId,
      vehicleTicketCode,
      check,
    }: {
      companyId: string;
      vehicleTicketCode: string;
      check: string;
    }) => {
      openModal(
        <UpdateVehicleTicketConfigComponent
          companyId={companyId}
          vehicleTicketCode={vehicleTicketCode}
          check={check}
        />,
        { key: updateVehicleTicketModalKey, type: 'action-sheet-full' },
      );
    },
    close: () => {
      closeModal(updateVehicleTicketModalKey);
    },
  };
};

const UpdateVehicleTicketConfigComponent = ({
  companyId,
  vehicleTicketCode,
  check,
}: {
  companyId: string;
  vehicleTicketCode: string;
  check: string;
}) => {
  const formRef = useRef(new FormHandler());
  const { closeModal } = useModal();

  const { data, isLoading: isLoadingForm } =
    companyConfigApi.useUpdateFerryCompanyVehicleConfigFormConfigQuery({
      companyId,
      ticketCode: vehicleTicketCode,
      check,
    });

  const [updateVehicleTicketConfig, { isLoading: isUpdating }] =
    companyConfigApi.useUpdateFerryCompanyVehicleConfigMutation();
  if (!data) {
    return null;
  }
  const { check: formCheck } = data;

  return (
    <Modal.Container>
      <Modal.Header
        title={translate('companyConfig.updateVehicleTicket')}
        onPress={() => closeModal(updateVehicleTicketModalKey)}
      />
      <ModalScrollView>
        <FormContent isLoading={isLoadingForm} form={data.config} flex={1}>
          <FormBuilder data={data.config} form={formRef.current} />
        </FormContent>
      </ModalScrollView>
      <Modal.Footer
        divider
        isLoading={isUpdating}
        onConfirm={async () => {
          const { valid, value } =
            formRef.current.getValue<
              CompanyConfigClientCreateFerryCompanyVehicleTicketRequest['config']
            >();

          if (valid) {
            const response = await updateVehicleTicketConfig({
              config: value,
              companyId,
              check: formCheck,
            });
            handleResponse({
              response,
              onSuccess: () => {
                closeModal(updateVehicleTicketModalKey);
              },
              onError: () => {
                closeModal(updateVehicleTicketModalKey);
              },
            });
          }
        }}
        onCancel={() => closeModal(updateVehicleTicketModalKey)}
      />
    </Modal.Container>
  );
};
